/**
 * @file Firestore-side ExportSession operations on the Web. Mirrors what the
 * mobile `ExportSessionRepository` (Dart) does for `awaiting_approval` /
 * `consumed` / `completed` / `failed` transitions.
 *
 * Web only ever pushes into the state machine the transitions allowed by
 * `firestore.rules` for the Web side:
 *   - `pending → awaiting_approval` (write web_client_info)
 *   - `approved → consumed` (null out encrypted_kek)
 *   - `consumed → completed | failed` (with optional failure_reason)
 */

import {
  type Firestore,
  type Unsubscribe,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import type { ExportFailureCategory, ExportSessionDoc, WebClientInfoDoc } from "../types";

const COLLECTION = "exportSessions";

export type SessionEvent =
  | { kind: "snapshot"; session: ExportSessionDoc & { id: string } }
  | { kind: "missing" }
  | { kind: "error"; error: unknown };

export function subscribeSession(
  firestore: Firestore,
  sessionId: string,
  onEvent: (event: SessionEvent) => void,
): Unsubscribe {
  const ref = doc(firestore, COLLECTION, sessionId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onEvent({ kind: "missing" });
        return;
      }
      const data = snap.data() as ExportSessionDoc;
      onEvent({ kind: "snapshot", session: { ...data, id: snap.id } });
    },
    (error) => {
      onEvent({ kind: "error", error });
    },
  );
}

/**
 * Move `pending → awaiting_approval` and persist client info on the session.
 * Called once after we've parsed the URL fragment and confirmed the user is
 * authenticated.
 */
export async function recordWebClientArrival(
  firestore: Firestore,
  sessionId: string,
  userAgent: string,
): Promise<void> {
  const ref = doc(firestore, COLLECTION, sessionId);
  const webClientInfo: Partial<WebClientInfoDoc> = {
    user_agent: userAgent,
  };
  await updateDoc(ref, {
    status: "awaiting_approval",
    // serverTimestamp() works as a sentinel inside updateDoc.
    web_client_info: {
      ...webClientInfo,
      connected_at: serverTimestamp(),
    },
  });
}

/**
 * Move `approved → consumed` and null out `encrypted_kek` (一発使用)。
 * Caller must have already imported the KEK into WebCrypto before invoking.
 */
export async function markSessionConsumed(firestore: Firestore, sessionId: string): Promise<void> {
  const ref = doc(firestore, COLLECTION, sessionId);
  await updateDoc(ref, {
    status: "consumed",
    encrypted_kek: null,
    consumed_at: serverTimestamp(),
  });
}

export async function markSessionCompleted(firestore: Firestore, sessionId: string): Promise<void> {
  const ref = doc(firestore, COLLECTION, sessionId);
  await updateDoc(ref, {
    status: "completed",
    completed_at: serverTimestamp(),
  });
}

export async function markSessionFailed(
  firestore: Firestore,
  sessionId: string,
  category: ExportFailureCategory,
): Promise<void> {
  const ref = doc(firestore, COLLECTION, sessionId);
  await updateDoc(ref, {
    status: "failed",
    failure_reason: category,
  });
}
